import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SubmissionPhotoField } from "@/components/SubmissionPhotoField";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["AB","Alberta"],["BC","British Columbia"],["MB","Manitoba"],["NB","New Brunswick"],
  ["NL","Newfoundland and Labrador"],["NS","Nova Scotia"],["ON","Ontario"],["PE","Prince Edward Island"],
  ["QC","Quebec"],["SK","Saskatchewan"],
];

export default function Submit() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [kind, setKind] = useState("new_depot");
  const [creditPublicly, setCreditPublicly] = useState(true);
  const [photo, setPhoto] = useState(null); // { photo_data, photo_caption } | null
  const [form, setForm] = useState({
    name: "",
    city: "",
    state_code: "",
    latitude: "",
    longitude: "",
    railroad: "",
    year_built: "",
    address: "",
    description: "",
    notes: "",
    contributor_name: "",
    contributor_email: "",
  });

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    if (!form.contributor_name.trim() || !form.contributor_email.trim()) {
      toast.error("Please tell us your name and email so we can credit you and follow up.");
      return false;
    }
    if (!form.contributor_email.includes("@")) {
      toast.error("That email doesn't look right.");
      return false;
    }
    if (!form.name.trim() || !form.city.trim() || !form.state_code) {
      toast.error("Location name, city, and state are required");
      return false;
    }
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (isNaN(lat) || lat < -90 || lat > 90 || isNaN(lng) || lng < -180 || lng > 180) {
      toast.error("Latitude must be -90..90 and longitude -180..180");
      return false;
    }
    return true;
  };

  const stateName = (code) => (US_STATES.find(([c]) => c === code) || [null, ""])[1];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name.trim(),
      city: form.city.trim(),
      state_code: form.state_code,
      state: stateName(form.state_code),
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      railroad: form.railroad.trim() || null,
      year_built: form.year_built ? parseInt(form.year_built, 10) : null,
      address: form.address.trim() || null,
      description: form.description.trim() || null,
    };

    try {
      setSaving(true);
      await axios.post(
        `${API}/submissions`,
        {
          kind,
          payload,
          notes: form.notes.trim() || null,
          contributor_name: form.contributor_name.trim(),
          contributor_email: form.contributor_email.trim(),
          credit_publicly: creditPublicly,
          photo_data: photo?.photo_data || null,
          photo_caption: photo?.photo_caption || null,
        },
        { withCredentials: true }
      );
      toast.success(
        photo
          ? "Thanks — your submission and photo are queued for review."
          : "Thanks — your submission is in the moderation queue."
      );
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/5"
              data-testid="submit-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-serif font-bold text-lg text-white">Contribute a Location</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="bg-[#111] border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-500" />
              New {kind === "new_tower" ? "Interlocking Tower" : "Depot"}
            </CardTitle>
            <p className="text-sm text-white/60 pt-2">
              An editor will review your submission before it appears on the map.
              We&apos;ll never publish your email address.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-6" data-testid="kind-toggle">
              {[
                ["new_depot", "Depot"],
                ["new_tower", "Interlocking Tower"],
              ].map(([k, label]) => (
                <Button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  variant={kind === k ? "default" : "outline"}
                  className={
                    kind === k
                      ? "bg-amber-500 hover:bg-amber-600 text-black"
                      : "border-white/10 text-white hover:bg-white/5"
                  }
                  data-testid={`kind-${k}-btn`}
                >
                  {label}
                </Button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Contributor */}
              <div className="p-4 bg-white/5 rounded-lg border border-white/5 space-y-3">
                <p className="text-[11px] text-amber-500 uppercase tracking-[0.2em] font-semibold">
                  About you
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Your name *" htmlFor="cn">
                    <Input id="cn" data-testid="submit-cname" value={form.contributor_name} onChange={handleChange("contributor_name")} className="bg-white/5 border-white/10 text-white" />
                  </Field>
                  <Field label="Your email *" htmlFor="ce">
                    <Input id="ce" type="email" data-testid="submit-cemail" value={form.contributor_email} onChange={handleChange("contributor_email")} className="bg-white/5 border-white/10 text-white" placeholder="never shown publicly" />
                  </Field>
                </div>
                <label className="flex items-start gap-2 pt-1 cursor-pointer group" data-testid="credit-toggle">
                  <input
                    type="checkbox"
                    checked={creditPublicly}
                    onChange={(e) => setCreditPublicly(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 accent-amber-500"
                  />
                  <span className="text-sm text-white/80">
                    Credit me publicly on the{" "}
                    <span className="text-amber-400">Contributors</span> page under my name above.
                    <span className="block text-xs text-white/50 mt-0.5">
                      Your email is never shown. You can turn this off anytime by emailing us.
                    </span>
                  </span>
                </label>
              </div>

              <Field label="Location name *" htmlFor="name">
                <Input id="name" data-testid="submit-name" value={form.name} onChange={handleChange("name")} className="bg-white/5 border-white/10 text-white" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="City *" htmlFor="city">
                  <Input id="city" data-testid="submit-city" value={form.city} onChange={handleChange("city")} className="bg-white/5 border-white/10 text-white" />
                </Field>
                <Field label="State *" htmlFor="state_code">
                  <select
                    id="state_code"
                    data-testid="submit-state"
                    value={form.state_code}
                    onChange={handleChange("state_code")}
                    className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3"
                  >
                    <option value="">Select…</option>
                    {US_STATES.map(([code, name]) => (
                      <option key={code} value={code} className="bg-[#111]">
                        {code} — {name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Latitude *" htmlFor="lat">
                  <Input id="lat" data-testid="submit-lat" type="number" step="0.000001" value={form.latitude} onChange={handleChange("latitude")} placeholder="e.g. 41.8781" className="bg-white/5 border-white/10 text-white" />
                </Field>
                <Field label="Longitude *" htmlFor="lng">
                  <Input id="lng" data-testid="submit-lng" type="number" step="0.000001" value={form.longitude} onChange={handleChange("longitude")} placeholder="e.g. -87.6298" className="bg-white/5 border-white/10 text-white" />
                </Field>
              </div>

              <p className="text-xs text-white/50 -mt-2">
                Tip: right-click a spot in Google Maps to copy exact coordinates.
              </p>

              <Field label="Railroad" htmlFor="railroad">
                <Input id="railroad" data-testid="submit-railroad" value={form.railroad} onChange={handleChange("railroad")} placeholder="e.g. Union Pacific" className="bg-white/5 border-white/10 text-white" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Year Built" htmlFor="year">
                  <Input id="year" data-testid="submit-year" type="number" value={form.year_built} onChange={handleChange("year_built")} placeholder="1890" className="bg-white/5 border-white/10 text-white" />
                </Field>
                <Field label="Address" htmlFor="addr">
                  <Input id="addr" data-testid="submit-address" value={form.address} onChange={handleChange("address")} className="bg-white/5 border-white/10 text-white" />
                </Field>
              </div>

              <Field label="Description" htmlFor="desc">
                <Textarea id="desc" data-testid="submit-description" value={form.description} onChange={handleChange("description")} rows={3} className="bg-white/5 border-white/10 text-white" />
              </Field>

              <SubmissionPhotoField value={photo} onChange={setPhoto} />

              <Field label="Notes for the reviewer" htmlFor="notes">
                <Textarea id="notes" data-testid="submit-notes" value={form.notes} onChange={handleChange("notes")} rows={2} placeholder="Sources, photos, anything that helps us verify." className="bg-white/5 border-white/10 text-white" />
              </Field>

              <div className="flex justify-end gap-3 pt-4">
                <Link to="/">
                  <Button type="button" variant="outline" className="border-white/10 text-white hover:bg-white/5">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="submit-submit-btn"
                >
                  {saving ? "Submitting…" : "Submit for review"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm text-white/80 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
